<script setup lang="ts">
/**
 * Language switcher (W3.M5).
 *
 * Two renderings of one control, because the two places that need it look
 * nothing alike: a `select` inside the Settings form, and a pair of inline
 * links under the sign-in card. The login screen matters — a Bulgarian user
 * who cannot read the English login page has no way to reach Settings to fix
 * it, so the switcher has to exist before authentication does.
 *
 * The choice is persisted by `setLocale()`, the only writer of that key — and,
 * for a signed-in person, also saved to their account (W11), because emails
 * and notifications are written in the language stored there, not in this
 * browser's. On the login screen there is no account yet, so the browser's
 * copy is the only one.
 */
import { computed } from 'vue'
import { LOCALE_NAMES, SUPPORTED, activeLocale, setLocale } from '@/i18n'
import type { Locale } from '@/i18n'
import { useAuthStore } from '@/stores/auth.store'
import { useSettingsStore } from '@/stores/settings.store'

const props = withDefaults(defineProps<{ variant?: 'select' | 'inline'; id?: string }>(), {
  variant: 'select',
  id: 'locale-switch',
})

const current = computed(() => activeLocale())
const locales = SUPPORTED

const auth = useAuthStore()
const settings = useSettingsStore()

function choose(locale: Locale | string) {
  setLocale(locale as Locale)
  if (auth.isAuthenticated) void settings.saveLocale(locale as Locale)
}
</script>

<template>
  <select
    v-if="props.variant === 'select'"
    :id="props.id"
    class="input"
    :value="current"
    @change="choose(($event.target as HTMLSelectElement).value)"
  >
    <option v-for="loc in locales" :key="loc" :value="loc">{{ LOCALE_NAMES[loc] }}</option>
  </select>

  <div v-else class="locale-switch">
    <button
      v-for="loc in locales"
      :key="loc"
      type="button"
      class="locale-switch__opt"
      :class="{ 'is-active': current === loc }"
      :aria-current="current === loc ? 'true' : undefined"
      :lang="loc"
      @click="choose(loc)"
    >
      {{ LOCALE_NAMES[loc] }}
    </button>
  </div>
</template>

<style scoped>
/* New UI with no counterpart in the mockup, so it carries its own styles —
 * built from the design tokens, never from hard-coded colours (D-017). */
.locale-switch {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  font-size: var(--fs-sm);
}
.locale-switch__opt {
  background: none;
  border: 0;
  padding: 2px 4px;
  cursor: pointer;
  color: var(--color-text-muted);
  border-radius: var(--radius-xs);
}
.locale-switch__opt:hover {
  color: var(--color-text-secondary);
}
.locale-switch__opt.is-active {
  color: var(--color-sage-deep);
  font-weight: var(--fw-semibold);
}
</style>
